import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {
  BaseGridCreationStructuredTableAdapter,
  GridCreationTableMetadata,
  GridCreationTableRowMetadata,
} from 'notebook/integrations/samples/BaseGridCreationStructuredTableAdapter';
import {getUnfinalizedParentStorablePrimarySample} from 'notebook/integrations/samples/InventoryCapacityValidationFunctions';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {createBoxesActionFunction} from 'notebook/structured-tables/action-functions/createBoxesActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {suggestedPlotsOnlyActionFunction} from 'notebook/structured-tables/action-functions/suggestedPlotsOnlyActionFunction';
import BoxCreationTableRowUpdater from 'notebook/structured-tables/row-updaters/BoxCreationTableRowUpdater';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {
  ActionFunctions,
  BaseStructuredTable,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {getStorableInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';
import {BoxSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import createBoxesRowsActionFunction from '../../structured-tables/action-functions/createBoxesRowsActionFunction';

/**
 * A BoxCreation table contains all of the schema fields needed for
 * a particular box as well as a name for the box and an optional
 * barcode column, depending on the settings in the BoxCreationTableMetadata.
 */

export type BoxCreationTableMetadata = GridCreationTableMetadata;

export type BoxCreationTableRowMetadata = GridCreationTableRowMetadata;

export class BoxCreationStructuredTableAdapter extends BaseGridCreationStructuredTableAdapter {
  getDisplayName(_: StructuredTable<BoxCreationTableMetadata, BoxCreationTableRowMetadata>): string {
    return 'Box Creation';
  }

  /**
   * Returns all supported columns for the schemas provided but always
   * removes the StageRun column, if present.  Additionally, we need
   * to always show the following columns:
   *
   * - Location -- the parent location of the box to be created
   * - Box -- the name we want the box to have
   *
   * Additionally, if the `shouldAutogenerateBarcode` field in the
   * metadata is false, then we will show a Barcode column where the
   * user can enter the barcode they would like the box to have.
   */
  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: BoxCreationTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const columns = super._getAllSupportedColumns({
      entryId,
      metadata,
      schemas,
      selectorsById,
      structuredTableInsertionContext,
    });

    return [
      ...(!metadata.requireCustomName
        ? [
            {
              ...makeStructuredTableColumn({
                columnType: TableColumnType.BOX,
                name: 'Box Name',
                defaultValue: {text: 'New Box'},
              }),
              isReadOnly: true,
            },
          ]
        : [
            {
              ...makeStructuredTableColumn({
                columnType: TableColumnType.BOX,
                name: 'Box Name',
              }),
              isRequired: true,
            },
          ]),
      ...(metadata.shouldAutogenerateBarcode
        ? []
        : [
            {
              ...makeStructuredTableColumn({
                columnType: TableColumnType.STRING,
                name: 'Barcode',
              }),
              isRequired: true,
            },
          ]),
      makeStructuredTableColumn({
        columnType: TableColumnType.PARENT_STORABLE,
        name: 'Parent Location',
        isRequired: true,
      }),
      ...columns,
    ];
  }

  override getActionFunctions(
    tableMetadata: BoxCreationTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<BoxCreationTableMetadata, BoxCreationTableRowMetadata> {
    const inherited = super.getActionFunctions(tableMetadata, schemas);
    return {
      ...inherited,
      primary: [createBoxesActionFunction],
      secondary: [...(inherited.secondary ?? []), suggestedPlotsOnlyActionFunction],
      // TODO: Determine if this use of ! is safe.
      overflow: [...inherited.overflow!, printBarcodesActionFunction],
      contextMenu: [createBoxesRowsActionFunction],
      footer: inherited.footer ?? [],
    };
  }

  // Keep in sync with BoxCreationTableInstantiator.get_primary_sample_ids_for_row (which delegates to
  // GridCreationTableInstantiator._get_storable_creation_primary_sample_ids) in
  // benchling/notebook/structured_tables/instantiators/box_or_plate_creation_table_instantiator.py (tested by
  // TestBoxCreationInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/box_or_plate_creation_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: StructuredTable<BoxCreationTableMetadata, BoxCreationTableRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const columnIndex = structuredTable.columns.findIndex(
      ({columnType}) => columnType === TableColumnType.BOX
    );
    assert(columnIndex !== -1, 'Expected structured table to have a BOX column ');
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};
    const schema = structuredTable.schemas[0];

    if (schema == null) {
      // Most likely, the user doesn't have permissions on the schema
      for (const {rowId} of structuredTable.rows) {
        primarySamplesByRowId[rowId] = [];
      }
      return primarySamplesByRowId;
    }

    structuredTable.rows.forEach(({rowId, cells, isFinalized}, rowIndex) => {
      // If row is not finalized, fetch parent storable if present. This is used to refresh the queries
      // for total & occupied capacity that power the cross-row capacity validation
      if (!isFinalized) {
        primarySamplesByRowId[rowId] = getUnfinalizedParentStorablePrimarySample({
          structuredTable,
          computedTable,
          rowIndex,
        });
        return;
      }
      // Box will only be present in finalized rows, as it has not yet been created in unfinalized ones
      const typedValue =
        computedTable != null ? computedTable[rowIndex][columnIndex]?.value : cells[columnIndex].typedValue;
      const box: StorableInfo | null = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
      if (box) {
        primarySamplesByRowId[rowId] = [
          {
            schemaId: schema.id,
            columnGroup: null,
            sampleLookupInfo: {sampleType: PrimarySampleLookupType.DIRECT, idForLookup: box.id},
          },
        ];
      } else {
        primarySamplesByRowId[rowId] = [];
      }
    });
    return primarySamplesByRowId;
  }

  override getTableKindRowUpdater(): typeof BoxCreationTableRowUpdater {
    return BoxCreationTableRowUpdater;
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: BoxCreationStructuredTable,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<StructuredTableAddOperation<BoxCreationTableMetadata, BoxCreationTableRowMetadata>> {
    const ops: Array<StructuredTableAddOperation<BoxCreationTableMetadata, BoxCreationTableRowMetadata>> = [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
        onSelect: async ({structuredTable, addFullRows}) => {
          const boxSchema = structuredTable.schemas[0] as BoxSchema;
          assert(
            boxSchema != null,
            'Tried to import spreadsheet into box creation table with inaccessible or nonexistent schema'
          );
          const rows = await importRowsFromSpreadsheet({
            structuredTable,
            schemas: {boxSchema},
            getSpreadsheetTypeForColumn: (column) =>
              getSpreadsheetTypeForColumn(ModelTypeConstants.BOX_SCHEMA, column),
          });

          if (rows) {
            addFullRows(rows);
          }
        },
      },
    ];
    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(
            structuredTable,
            this,
            addSamples,
            WORKLIST_FILL_LOCATION.BOX_CREATION_TABLE
          );
        },
      });
    }

    if (BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_USING_AI,
        onSelect: ({structuredTable}) => {
          addAIDataEntryRows?.(undefined, structuredTable.name);
        },
      });
    }
    return ops;
  }

  override enableCustomColumns(structuredTable: BoxCreationStructuredTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: BoxCreationStructuredTable): boolean {
    return true;
  }
}

export type GenericBoxCreationStructuredTable<TIds> = BaseStructuredTable<
  BoxCreationTableMetadata,
  BoxCreationTableRowMetadata,
  TIds
>;

export type BoxCreationStructuredTable = GenericBoxCreationStructuredTable<ClientId>;
export type FromServerBoxCreationStructuredTable = GenericBoxCreationStructuredTable<FromServerId>;
export type ToServerBoxCreationStructuredTable = GenericBoxCreationStructuredTable<ToServerId>;
